// Currency-agnostic money formatting.
//
// Why we don't use "k" / "L" / "Cr" abbreviations: those are dialect-specific.
// "L" (lakh) and "Cr" (crore) only make sense in Indian English; "k" suggests
// Western thousands. The moment a tenant onboards a USD/AED/NZD client, the
// abbreviations stop matching the user's mental model. Salesforce + HubSpot
// converged on full numbers with locale-aware comma grouping precisely because
// it's universal — INR shows as "1,23,456" (Indian grouping) while USD shows
// as "1,234,567" (Western grouping), driven by the currency, not by hand-coded
// thresholds in the UI.
//
// Output format: "<CURRENCY_CODE> <grouped_number>" e.g. "INR 1,23,456" or
// "USD 1,234,567". We use currencyDisplay='code' so the prefix is the ISO
// 4217 code rather than a glyph — clearer for non-Indian users seeing INR
// for the first time and consistent across all currencies.

const LOCALE_BY_CURRENCY = {
  INR: 'en-IN',
  USD: 'en-US',
  GBP: 'en-GB',
  EUR: 'en-GB',
  AED: 'en-US',
  NZD: 'en-NZ',
  AUD: 'en-AU',
  CAD: 'en-CA',
};

export function formatMoney(amount, currency = 'INR') {
  if (amount == null || !Number.isFinite(Number(amount))) {
    return `${currency} 0`;
  }
  const locale = LOCALE_BY_CURRENCY[currency] || 'en-US';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(Number(amount));
  } catch {
    // Unknown currency code — fall back to manual grouping
    return `${currency} ${Number(amount).toLocaleString(locale)}`;
  }
}

// Compact variant for very tight UI slots (table cells with little room).
// Still no abbreviations — just clips decimals. Use sparingly; the default
// formatMoney is preferred everywhere.
export function formatMoneyCompact(amount, currency = 'INR') {
  return formatMoney(Math.round(Number(amount) || 0), currency);
}

// ── Amount in words ──────────────────────────────────────────────────────
//
// Praxia's ask: a fee of 150000000 should also read as "15 crore", so the
// magnitude is legible at a glance on a lead card and cannot be misread by a
// digit. Shown ALONGSIDE the numeric value, never instead of it.
//
// This does not contradict the no-abbreviations rule above. That rule keeps
// the NUMBER itself universal and locale-grouped; this is a separate,
// explicitly-labelled human reading of the same figure. And it is
// locale-aware for the same reason the grouping is: an INR amount reads in
// lakh/crore, everything else in million/billion. A UAE tenant seeing
// "15 crore" would be exactly the failure the rule warns about.
// No "thousand" tier on purpose: "45,000 (45 thousand)" restates the digits
// without clarifying them. Words earn their place only where a long run of
// zeros is genuinely easy to misread — lakh/crore and million/billion.
const INDIAN_SCALES = [
  [10000000, 'crore'],
  [100000, 'lakh'],
];

const WESTERN_SCALES = [
  [1000000000, 'billion'],
  [1000000, 'million'],
];

/** Trim a float to at most 2dp with no trailing zeros: 1.50 → "1.5", 2.00 → "2". */
function _trim(n) {
  return String(Number(n.toFixed(2)));
}

/**
 * amountInWords(15000000, 'INR')  → "1.5 crore"
 * amountInWords(150000000, 'INR') → "15 crore"
 * amountInWords(1500000, 'USD')   → "1.5 million"
 * amountInWords(45000, 'INR')     → ""   (small enough to read as digits)
 */
export function amountInWords(amount, currency = 'INR') {
  const n = Number(amount);
  if (!Number.isFinite(n) || n === 0) return '';
  const abs = Math.abs(n);
  const scales = currency === 'INR' ? INDIAN_SCALES : WESTERN_SCALES;
  for (const [size, name] of scales) {
    if (abs >= size) {
      const value = _trim(abs / size);
      // "1 crore", not "1 crores" — the unit is invariant in Indian English
      // usage on documents, and pluralising only some of them reads worse.
      return `${n < 0 ? '-' : ''}${value} ${name}`;
    }
  }
  return '';   // below a lakh / a million: the digits already say it
}

/** "INR 1,50,00,000 (1.5 crore)" — the numeric form is always primary. */
export function formatMoneyWithWords(amount, currency = 'INR') {
  const numeric = formatMoney(amount, currency);
  const words = amountInWords(amount, currency);
  return words ? `${numeric} (${words})` : numeric;
}
